import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Plus, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Product } from '@/data/products';
import { useCart } from '@/context/CartContext';
import { useToast } from '@/hooks/use-toast';
import { useCurrency } from '@/context/CurrencyContext';
import { getCartRecommendations } from '@/services/productService';
import ProductImage from '@/components/ProductImage';
import { motion, AnimatePresence } from 'framer-motion';

interface CartRecommendationsProps {
  cartProductIds: string[];
}

export function CartRecommendations({ cartProductIds }: CartRecommendationsProps) {
  const [recommendations, setRecommendations] = useState<Product[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [fetchingAfterAdd, setFetchingAfterAdd] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchedIds = useRef<string>('');
  
  const { addToCart } = useCart();
  const { toast } = useToast();
  const { selectedCurrency, getConvertedItemPrice, formatDisplayAmount } = useCurrency();
  const navigate = useNavigate();

  // Memoize the cart product IDs string to prevent unnecessary re-fetches
  const cartProductIdsString = useMemo(() => 
    cartProductIds.sort().join(','), 
    [cartProductIds]
  );

  // Memoized fetch function
  const fetchRecommendations = useCallback(async (isReplacementFetch = false) => {
    if (cartProductIds.length === 0) {
      setRecommendations([]);
      setInitialLoading(false);
      setFetchingAfterAdd(false);
      return;
    }

    // Skip if we already fetched for the same cart items (unless it's a replacement fetch)
    if (!isReplacementFetch && lastFetchedIds.current === cartProductIdsString) {
      return;
    }

    try {
      if (isReplacementFetch) {
        setFetchingAfterAdd(true);
      } else {
        setInitialLoading(true);
      }
      
      const products = await getCartRecommendations(cartProductIds, 10);
      // Exclude out-of-stock items (treat undefined/null as zero to stay conservative)
      const filtered = products.filter(p => (p.stockQuantity ?? 0) > 0);
      setRecommendations(filtered);
      setCurrentIndex(0);
      lastFetchedIds.current = cartProductIdsString;
    } catch (error) {
      console.error('Error fetching cart recommendations:', error);
      setRecommendations([]);
    } finally {
      if (isReplacementFetch) {
        setFetchingAfterAdd(false);
      } else {
        setInitialLoading(false);
      }
    }
  }, [cartProductIds, cartProductIdsString]);

  // Fetch recommendations when cart changes
  useEffect(() => {
    fetchRecommendations(false);
  }, [fetchRecommendations]);

  // Auto-advance functionality
  useEffect(() => {
    if (recommendations.length <= 1 || isPaused || addingToCart || fetchingAfterAdd) {
      return;
    }

    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => prev < recommendations.length - 1 ? prev + 1 : 0);
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [recommendations.length, isPaused, addingToCart, fetchingAfterAdd]);

  // Pause auto-advance on hover/interaction
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  // Auto-advance to next product when current one is added to cart
  useEffect(() => {
    if (recommendations.length > 0 && currentIndex >= recommendations.length) {
      setCurrentIndex(Math.max(0, recommendations.length - 1));
    }
  }, [recommendations.length, currentIndex]);

  const handleAddToCart = useCallback(async (product: Product) => {
    try {
      setAddingToCart(true);
      
      // Use the same addToCart signature as ProductDetail page: (product: Product, quantity: number)
      addToCart(product, 1);

      // Remove the added product from recommendations immediately
      const updatedRecommendations = recommendations.filter(p => p.id !== product.id);
      setRecommendations(updatedRecommendations);

      // Auto-advance to next product or stay at current index if it was the last one
      if (currentIndex >= updatedRecommendations.length && updatedRecommendations.length > 0) {
        setCurrentIndex(updatedRecommendations.length - 1);
      }

      toast({
        title: "Added to Cart",
        description: `${product.name} has been added to your cart.`,
      });

      // Fetch replacement recommendations if we're running low (only when needed)
      if (updatedRecommendations.length <= 3) {
        await fetchRecommendations(true);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: "Error",
        description: "Failed to add item to cart. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAddingToCart(false);
    }
  }, [addToCart, recommendations, currentIndex, toast, fetchRecommendations]);

  const handlePrevious = () => {
    setIsPaused(true);
    setCurrentIndex(prev => prev > 0 ? prev - 1 : recommendations.length - 1);
    setTimeout(() => setIsPaused(false), 1000); // Resume auto-advance after 1 second
  };

  const handleNext = () => {
    setIsPaused(true);
    setCurrentIndex(prev => prev < recommendations.length - 1 ? prev + 1 : 0);
    setTimeout(() => setIsPaused(false), 1000); // Resume auto-advance after 1 second
  };

  const handleProductClick = useCallback((product: Product) => {
    navigate(`/product/${product.id}`);
  }, [navigate]);

  const currentProduct = recommendations[currentIndex];

  // Don't render if initially loading or no recommendations
  if (initialLoading) {
    return (
      <div className="relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border border-white/40 p-4 mb-6 hover:shadow-2xl transition-all duration-500 group">
        {/* Animated Border Accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-t-2xl"></div>
        <h3 className="text-lg font-bold text-gray-900 mb-3">You might also like</h3>
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-jamaica-green" />
          <span className="ml-2 text-sm text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  if (!currentProduct || recommendations.length === 0) {
    return null;
  }

  const effectivePrice = currentProduct.onSale && currentProduct.onSalePrice && currentProduct.onSalePrice < currentProduct.price
    ? currentProduct.onSalePrice
    : currentProduct.price;

  return (
    <div 
      className="relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border border-white/40 p-4 mb-6 hover:shadow-2xl transition-all duration-500 group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Animated Border Accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-t-2xl"></div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-bold text-gray-900">You might also like</h3>
          {fetchingAfterAdd && (
            <div className="flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin text-jamaica-green" />
              <span className="text-xs text-gray-500">Loading more...</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePrevious}
            disabled={recommendations.length <= 1}
            className="h-6 w-6 p-0 hover:bg-gray-100"
          >
            <ChevronLeft className="h-3 w-3" />
          </Button>
          <span className="text-xs text-gray-500 px-2">
            {currentIndex + 1}/{recommendations.length}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNext}
            disabled={recommendations.length <= 1}
            className="h-6 w-6 p-0 hover:bg-gray-100"
          >
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden border border-gray-200 hover:border-jamaica-green/30 transition-all duration-300">
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentProduct.id}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ 
                duration: 0.3, 
                ease: [0.4, 0, 0.2, 1]
              }}
              className="flex items-center p-3 gap-3"
            >
              {/* Product Image - Clickable */}
              <div 
                className="w-16 h-16 flex-shrink-0 bg-gray-50 rounded-md overflow-hidden cursor-pointer hover:ring-2 hover:ring-jamaica-green/30 transition-all duration-200"
                onClick={() => handleProductClick(currentProduct)}
              >
                <ProductImage
                  src={currentProduct.image}
                  alt={currentProduct.name}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                />
              </div>

              {/* Product Details - Clickable */}
              <div 
                className="flex-1 min-w-0 cursor-pointer"
                onClick={() => handleProductClick(currentProduct)}
              >
                {/* Product Name and Badges */}
                <div className="flex items-start gap-2 mb-2">
                  <h4 className="text-sm font-medium text-gray-900 truncate whitespace-nowrap flex-1 hover:text-jamaica-green transition-colors duration-200">
                    {currentProduct.name}
                  </h4>
                  <div className="flex gap-1">
                    {currentProduct.isBestSeller && (
                      <Badge className="bg-jamaica-gold text-white text-xs px-1 py-0 h-4">
                        Bestseller
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Sale badge above price */}
                {currentProduct.onSale && (
                  <Badge className="bg-black text-jamaica-yellow text-[11px] px-2 py-0 h-5 w-fit mb-1">
                    Sale
                  </Badge>
                )}

                {/* Price Display */}
                <div className="flex items-center gap-1">
                  <span className="text-base font-bold text-jamaica-green hover:text-jamaica-green/80 transition-colors duration-200">
                    {formatDisplayAmount(getConvertedItemPrice(effectivePrice))} {selectedCurrency}
                  </span>
                  {currentProduct.onSale && currentProduct.onSalePrice && currentProduct.onSalePrice < currentProduct.price && (
                    <span className="text-xs text-gray-500 line-through">
                      {formatDisplayAmount(getConvertedItemPrice(currentProduct.price))}
                    </span>
                  )}
                </div>
              </div>

              {/* Add to Cart Button - Outside clickable area */}
              <div className="flex-shrink-0">
                <Button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent navigation when clicking Add to Cart
                    handleAddToCart(currentProduct);
                  }}
                  disabled={addingToCart}
                  size="sm"
                  className="bg-jamaica-green text-white hover:bg-jamaica-green/90 px-3 py-1 h-7 text-xs min-w-[80px]"
                >
                  {addingToCart ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="h-3 w-3 mr-1" />
                      Add
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </Card>

      {/* Progress Dots */}
      {recommendations.length > 1 && (
        <div className="flex justify-center mt-3 gap-1">
          {recommendations.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => {
                setIsPaused(true);
                setCurrentIndex(index);
                setTimeout(() => setIsPaused(false), 1000);
              }}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${
                index === currentIndex
                  ? 'bg-jamaica-green w-4'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              animate={{
                scale: index === currentIndex ? 1.1 : 1,
                backgroundColor: index === currentIndex ? '#00742F' : '#D1D5DB'
              }}
              transition={{ duration: 0.2 }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
